import { IconName } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tabs } from '@delmonicos/shared-design-system-react/src/components/tab/tabs.component';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';

import * as css from './charge-point-configuration.module.scss';
import { AuthenticationTabComponent } from './tabs/authentication-tab/authentication-tab.component';
import { ConfigurationTabComponent } from './tabs/configuration-tab/configuration-tab.component';
import { OcppConfigurationTabComponent } from './tabs/ocpp-configuration-tab/ocpp-configuration-tab.component';

enum TabsEnum {
  CONFIGURATION = 'configuration-tab',
  AUTHENTICATION = 'authentication-tab',
  OCPP_CONFIGURATION = 'ocpp-configuration-tab',
}

export const ChargePointConfigurationController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canReadOcppConfiguration =
    !!userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.CHARGE_POINTS,
      action: ActionsByResource.CHARGE_POINTS.OCPP_GET_CONFIGURATION,
    });

  // ACTIONS ///////////////////////////////////////////////////////////////////

  // Nothing

  // TABS //////////////////////////////////////////////////////////////////////

  const tabs = useMemo(() => {
    const elements: { title: string; icon: IconName; key: TabsEnum }[] = [
      {
        title: t('$.charge-point-configuration.configuration-tab.title'),
        icon: 'settings-01',
        key: TabsEnum.CONFIGURATION,
      },
      {
        title: t('$.charge-point-configuration.authentication-tab.title'),
        icon: 'log-in-02',
        key: TabsEnum.AUTHENTICATION,
      },
    ];

    if (canReadOcppConfiguration) {
      elements.push({
        title: t('$.charge-point-configuration.ocpp-configuration-tab.title'),
        icon: 'settings-01',
        key: TabsEnum.OCPP_CONFIGURATION,
      });
    }

    return elements;
  }, [t, canReadOcppConfiguration]);

  const [currentTab, setCurrentTab] = useState<TabsEnum>(
    TabsEnum.CONFIGURATION,
  );

  const renderTab = () => {
    switch (currentTab) {
      case TabsEnum.CONFIGURATION:
        return <ConfigurationTabComponent />;
      case TabsEnum.AUTHENTICATION:
        return <AuthenticationTabComponent />;
      case TabsEnum.OCPP_CONFIGURATION:
        return <OcppConfigurationTabComponent />;
      default:
        // TODO: Refactor this display
        return <div>No tab selected</div>;
    }
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack
      className={css.chargePointConfiguration}
      direction="column"
      gap={GapEnum.L}>
      <Tabs
        currentTab={currentTab}
        elements={tabs}
        onTabClick={setCurrentTab}
      />
      {renderTab()}
    </Stack>
  );
};
